import React from "react";
import { Skeleton } from "../../Components/Needed";

const PanelSkeleton = () => (
    <div className="row g-2">
        {[0, 1, 2].map((i) => (
            <div className="col-4" key={i}><Skeleton height={64} /></div>
        ))}
    </div>
);

const MiniStat = ({ value, label }) => (
    <div className="db-mini-stat">
        <div className="db-mini-stat-value">{Number(value || 0).toLocaleString()}</div>
        <div className="db-mini-stat-label">{label}</div>
    </div>
);

// A simple stacked proportion bar used for the customer verification split -
// three semantic buckets (verified / pending / rejected), not an arbitrary
// categorical set, so green/amber/red carries real meaning here rather than
// doubling as the chart's accent color.
const ProportionBar = ({ segments }) => {
    const total = segments.reduce((sum, seg) => sum + seg.value, 0) || 1;
    return (
        <div className="stage-bar-track mb-3" style={{ height: 12, display: "flex" }}>
            {segments.map((seg) => (
                <span
                    key={seg.label}
                    style={{ width: `${(seg.value / total) * 100}%`, background: seg.color, height: "100%" }}
                    title={`${seg.label}: ${seg.value}`}
                />
            ))}
        </div>
    );
};

export const CustomerPanel = ({ data, loading }) => {
    if (loading) return <PanelSkeleton />;
    const { total = 0, fullyVerified = 0, pendingVerification = 0, rejected = 0 } = data || {};
    return (
        <div>
            <ProportionBar
                segments={[
                    { label: "Fully verified", value: fullyVerified, color: "#198754" },
                    { label: "Pending", value: pendingVerification, color: "#f89c2b" },
                    { label: "Rejected", value: rejected, color: "#dc3545" },
                ]}
            />
            <div className="row g-2">
                <div className="col-4"><MiniStat value={fullyVerified} label="Verified" /></div>
                <div className="col-4"><MiniStat value={pendingVerification} label="Pending" /></div>
                <div className="col-4"><MiniStat value={rejected} label="Rejected" /></div>
            </div>
            <div className="db-summary-line mt-2">Total customers: <strong>{total.toLocaleString()}</strong></div>
        </div>
    );
};

export const StaffPanel = ({ data, loading }) => {
    if (loading) return <PanelSkeleton />;
    const { total = 0, active = 0, suspended = 0, terminated = 0, byHub = [] } = data || {};
    return (
        <div>
            <div className="row g-2 mb-3">
                <div className="col-4"><MiniStat value={active} label="Active" /></div>
                <div className="col-4"><MiniStat value={suspended} label="Suspended" /></div>
                <div className="col-4"><MiniStat value={terminated} label="Terminated" /></div>
            </div>
            {byHub.length ? (
                <div>
                    <div className="db-panel-subtitle mb-1">Active staff by hub</div>
                    {byHub.slice(0, 5).map((row) => (
                        <div className="db-hub-row" key={row.hubId}>
                            <span>{row.hubId}</span>
                            <span className="fw-semibold">{row.count}</span>
                        </div>
                    ))}
                </div>
            ) : null}
            <div className="db-summary-line mt-2">Total staff: <strong>{total.toLocaleString()}</strong></div>
        </div>
    );
};

const FleetRow = ({ title, data }) => (
    <div className="mb-3">
        <div className="db-panel-subtitle mb-1">{title} ({Number(data?.total || 0).toLocaleString()})</div>
        <div className="row g-2">
            <div className="col-3"><MiniStat value={data?.active} label="Active" /></div>
            <div className="col-3"><MiniStat value={data?.verified} label="Verified" /></div>
            <div className="col-3"><MiniStat value={data?.pending} label="Pending" /></div>
            <div className="col-3"><MiniStat value={data?.rejected} label="Rejected" /></div>
        </div>
    </div>
);

export const FleetPanel = ({ data, loading }) => {
    if (loading) return <PanelSkeleton />;
    return (
        <div>
            <FleetRow title="Cars" data={data?.cars} />
            <FleetRow title="Bikes" data={data?.bikes} />
        </div>
    );
};
